import { ToastType } from '@components/Toast/Toast';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { FeatureFlags } from '@constants/featureFlags';
import { IsFeatureFlagEnabled } from '@store/Global/service';
import {
  carrierUpdateInsurance,
  fetchCarrierDetail,
  fetchCarrierFeatures,
} from '@store/orders/services/carrierServices';
import {
  carrierStore,
  orderStore,
  setCarrierLoading,
  setCarrierStore,
  setOrderStore,
} from '@store/orders/store';
import {
  Carrier,
  CarrierContactT,
  CarrierFeatures,
  TabState,
} from '@store/orders/types';
import { Contact } from '@typeDefinitions/contactTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';
import { produce } from 'solid-js/store';

import {
  updateLoadPropertyAtIndex,
  updateOrderState,
} from './orderFormActions';
import { copyLoad } from '../helper';

export const copyCarrier = (load: ILoadEditViewModel) => {
  //breaking these into local assignments, in case we need to alter
  // this copy logic later where more conditionals maybe needed
  const preserveCarrier = load.mode === 'LTL';
  const newLoad = copyLoad(load, preserveCarrier, false, true);

  const sequence =
    orderStore.order.loads.reduce(
      (max: number, existingLoad: { sequence: number }) =>
        Math.max(max, existingLoad.sequence),
      0,
    ) + 1;
  newLoad.sequence = sequence;
  newLoad.loadNum = `${load.orderId}-${sequence}`;
  updateOrderState({
    loads: [
      ...orderStore.order.loads,
      newLoad as unknown as ILoadEditViewModel,
    ],
  });
};

export const copyCarrierDetails = (
  carrier: Carrier,
  featureFlag: CarrierFeatures,
) => {
  const copiedLoadIndex = orderStore.order.loads.length - 1;
  const copiedLoad = orderStore.order.loads[copiedLoadIndex] as
    | ILoadEditViewModel
    | undefined;
  if (copiedLoad?.carrierId == null) return;

  setCarrierStore(
    produce((draft) => {
      draft.carrier[copiedLoadIndex] = carrier;
      draft.featureFlags[copiedLoadIndex] = featureFlag;
    }),
  );
};

export const setActiveCarrierTab = (tab: TabState) => {
  setOrderStore('activeTab', tab);
};

export const getCarrierDetail = async (
  Id: number,
  index: number,
): Promise<Carrier | undefined> => {
  setCarrierLoading(true);

  try {
    const carrierData = await fetchCarrierDetail(Id);
    setCarrierStore(
      produce((draft) => {
        draft.carrier[index] = carrierData;
      }),
    );

    if (
      (carrierData.hasExpiredCargoInsurance ||
        carrierData.hasExpiredLiabilityInsurance) &&
      carrierData.id
    ) {
      try {
        await checkCarrierUpdateInsurance(carrierData.id, index);
      } catch (error) {
        logError('checkCarrierUpdateInsurance', error as Error);
        handleToast(ToastType.Error, (error as Error).message);
      }
    }

    updateCarrierStoreContactsWithLoadCarrier(index);
    return carrierData;
  } catch (error) {
    logError('getCarrierDetail', error as Error);
    return undefined;
  } finally {
    setCarrierLoading(false);
  }
};

export const getCarrierFeatures = async (id: number, index: number) => {
  const featureFlags = await fetchCarrierFeatures(id);
  if (featureFlags) {
    setCarrierStore(
      produce((draft) => {
        draft.featureFlags[index] = featureFlags;
      }),
    );
  }
};

export const checkCarrierUpdateInsurance = async (
  id: number,
  index: number,
) => {
  const response = await carrierUpdateInsurance(
    id,
    CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsInsurance,
  );
  if (response.isSuccess) {
    if (
      !response.value.hasExpiredCargoInsurance ||
      !response.value.hasExpiredLiabilityInsurance
    ) {
      setCarrierStore('carrier', index, {
        hasExpiredCargoInsurance: response.value.hasExpiredCargoInsurance,
        hasExpiredLiabilityInsurance:
          response.value.hasExpiredLiabilityInsurance,
      });
      handleToast(
        ToastType.Success,
        // We should fix the linter to allow a way around this.
        //eslint-disable-next-line quotes
        "New Insurance Information found, updated Carrier's record",
      );
    } else
      handleToast(
        ToastType.Caution,
        'Auto Insurance update was unsuccessful, updated information was not found',
      );
  } else {
    handleToast(
      ToastType.Error,
      Array.isArray(response.errors) && response.errors.length > 0
        ? response.errors[0].message
        : 'Failed to update insurance information',
    );
  }
};

export const addNewContactToCarrierStoreAndUpdateOrderStore = (
  contact: Contact,
) => {
  setCarrierStore(
    'carrier',
    orderStore.activeTab.index,
    'contacts',
    (contacts) => [...contacts, contact as unknown as CarrierContactT],
  );
  updateLoadPropertyAtIndex({
    carrierContactId: contact.id,
  });
};

export const GetCarrierVerificationLockdownValue = async () => {
  const result = await IsFeatureFlagEnabled(
    FeatureFlags.CarrierVerificationLockdown,
  );

  setOrderStore(() => ({
    carrierContactLockdownEnabled: result,
  }));
};

export const updateCarrierStoreContactsWithLoadCarrier = (
  carrierIndex: number,
) => {
  const loadCarrierContact =
    orderStore.order.loads[carrierIndex]?.carrierContact;
  if (loadCarrierContact?.status !== 'Inactive') return;
  if (!(carrierStore.carrier[carrierIndex] as Carrier | undefined)) return;

  setCarrierStore('carrier', carrierIndex, 'contacts', (contacts) =>
    contacts.some((contact) => contact.id === loadCarrierContact.id)
      ? contacts
      : [...contacts, loadCarrierContact],
  );
};
